import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { useToast } from "@/hooks/use-toast";
import { useConfirmation } from "@/hooks/use-confirmation";
import { DocumentItem, DocumentData } from "@/lib/stores/documentStore";
import { ItemTypeConfig } from "@/pages/unified-items/configs/types";

export interface UseDocumentsHandlersProps {
  data: any[];
  selectedGame: any;
  config: ItemTypeConfig;
  deleteDocument: (id: string, gameId: string) => Promise<void>;
  createDocument: (data: any) => Promise<any>;
}

export function useDocumentsHandlers({
  data,
  selectedGame,
  config,
  deleteDocument,
  createDocument,
}: UseDocumentsHandlersProps) {
  const { toast } = useToast();
  const { confirmation } = useConfirmation();
  const navigate = useNavigate();

  const handleDelete = useCallback(
    async (id: string, gameId: string) => {
      const item = data.find((item: any) => item.id === id);
      const itemName = item?.name || "this item";

      const confirmDialog = confirmation({
        title: "Delete Document",
        description: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
        confirmText: "Delete",
        cancelText: "Cancel",
        variant: "destructive",
        onConfirm: async () => {
          try {
            await deleteDocument(id, gameId);
            confirmDialog.dismiss();
          } catch (error) {
            console.error("Error deleting document:", error);
            confirmDialog.dismiss();
            toast({
              title: "Error",
              description: "Failed to delete document",
              variant: "destructive",
            });
          }
        },
        onCancel: () => {
          confirmDialog.dismiss();
        },
      });
    },
    [data, confirmation, toast, deleteDocument]
  );

  const handleCardClick = useCallback(
    (card: CardData) => {
      navigate(`/documents/${card.id}`, { state: { itemName: card.title } });
    },
    [navigate]
  );

  const handleAdd = useCallback(() => {
    if (!selectedGame?.id) {
      toast({
        title: "Error",
        description: "No game selected",
        variant: "destructive",
      });
      return;
    }

    // Count existing untitled documents to generate the next number
    if (Array.isArray(data)) {
      const untitledDocs = (data as DocumentItem[]).filter(
        (doc: DocumentItem) => doc.name.startsWith("Untitled")
      );
      const nextNumber = untitledDocs.length + 1;
      const documentName = `Untitled-${nextNumber}`;

      const createAndNavigate = async () => {
        try {
          const newDocument = await createDocument({
            game_id: selectedGame.id,
            name: documentName,
            data: [] as DocumentData[],
            settings: {
              subtitle: "",
              type: "",
              group: "",
            },
            tags: [],
          });

          toast({
            title: "Success",
            description: "Document created successfully",
          });

          navigate(`/documents/${newDocument.id}`);
        } catch (error) {
          console.error("Error creating document:", error);
        }
      };

      createAndNavigate();
    }
  }, [selectedGame, data, toast, navigate, createDocument]);

  const handleDocumentCreated = useCallback(
    (documentId: string) => {
      navigate(`/documents/${documentId}`);
    },
    [navigate]
  );

  return {
    handleDelete,
    handleCardClick,
    handleAdd,
    handleDocumentCreated,
  };
}

